import { useContext, useEffect, useState } from 'react';
import { Badge, Button, Form, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import { withTranslation } from 'react-i18next';
import { BiPencil, BiTrash, BiSort } from 'react-icons/bi';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LoaderContext } from '../../../App';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';
import PaginationComponent from '../../../components/Pagination/Pagination';
import { SORT_ORDER_DESC } from '../../../core/constants/common';
import GHGEmissionMasterDataService from '../../../core/service/GHGEmissionMasterDataService';
import FactorFormModal from './FactorFormModal';

const GHGEmissionFactors = ({ t, datasetName, version, selectedSource, onClearSourceFilter, onAddFactor, onHideAddFactor, companyId = null }) => {
  const { setLoaderDisplay } = useContext(LoaderContext);
  
  const [factors, setFactors] = useState([]);
  const [factorsTotalCount, setFactorsTotalCount] = useState(0);
  const [factorsCurrentPage, setFactorsCurrentPage] = useState(1);
  const [factorsItemsPerPage, setFactorsItemsPerPage] = useState(10);
  const [order, setOrder] = useState(SORT_ORDER_DESC);
  const [sortField, setSortField] = useState('ghg');
  const [sourcesForFactors, setSourcesForFactors] = useState([]);
  const [showAddFactorModal, setShowAddFactorModal] = useState(false);
  const [showEditFactorModal, setShowEditFactorModal] = useState(false);
  const [showDeleteFactorModal, setShowDeleteFactorModal] = useState(false);
  const [selectedFactor, setSelectedFactor] = useState(null);
  const [factorToDelete, setFactorToDelete] = useState(null);
  const [factorFilters, setFactorFilters] = useState({
    emissionSourceId: '',
    ghg: ''
  });

  // Load sources list for dropdown
  useEffect(() => {
    if (datasetName && version) {
      loadSourcesForFactors();
    }
  }, [datasetName, version]);

  // Reset pagination when dataset or version changes
  useEffect(() => {
    setFactorsCurrentPage(1);
  }, [datasetName, version]);

  // Load factors data - handles all data fetching
  useEffect(() => {
    if (datasetName && version) {
      if (selectedSource) {
        // Don't paginate when viewing by source - load all factors
        loadFactorsBySource();
      } else {
        loadFactors();
      }
    } else {
      setFactors([]);
      setFactorsTotalCount(0);
    }
  }, [datasetName, version, factorsCurrentPage, factorsItemsPerPage, factorFilters, selectedSource, sortField, order]);

  useEffect(() => {
    if (onAddFactor !== undefined && onAddFactor) {
      setShowAddFactorModal(true);
    } else if (onAddFactor === false) {
      setShowAddFactorModal(false);
    }
  }, [onAddFactor]);

  const loadSourcesForFactors = () => {
    if (!datasetName || !version) return;
    GHGEmissionMasterDataService.listSources(datasetName, version, { pageIndex: 0, pageSize: 1000 }, companyId)
      .then((response) => {
        if (response.status === 200) {
          const data = response.data;
          setSourcesForFactors(data.content || data.data || data || []);
        }
      })
      .catch((error) => {
      });
  };

  const loadFactors = () => {
    if (!datasetName || !version) return;
    
    setLoaderDisplay(true);
    const params = {
      pageIndex: factorsCurrentPage - 1,
      pageSize: factorsItemsPerPage,
      sort: `${sortField},${order}`,
      ...Object.fromEntries(Object.entries(factorFilters).filter(([_, v]) => v !== ''))
    };

    GHGEmissionMasterDataService.listFactors(datasetName, version, params, companyId)
      .then((response) => {
        if (response.status === 200) {
          const data = response.data;
          const factorsList = data.content || data.data || data || [];
          setFactors(factorsList);
          const totalCount = parseFloat(response.headers["x-total-count"]);
          setFactorsTotalCount(totalCount);
        }
      })
      .catch((error) => {
      })
      .finally(() => {
        setLoaderDisplay(false);
      });
  };

  const loadFactorsBySource = () => {
    if (!datasetName || !version || !selectedSource) return;
    
    setLoaderDisplay(true);
    GHGEmissionMasterDataService.getFactorsBySource(datasetName, version, selectedSource.id, companyId)
      .then((response) => {
        if (response.status === 200) {
          const factorsList = response.data || [];
          setFactors(factorsList);
          setFactorsTotalCount(factorsList.length);
          setFactorsCurrentPage(1);
        }
      })
      .catch((error) => {
      })
      .finally(() => {
        setLoaderDisplay(false);
      });
  };

  const handleFactorFilterChange = (field, value) => {
    setFactorFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setFactorsCurrentPage(1);
  };

  const handleEditFactor = (factor) => {
    setSelectedFactor(factor);
    setShowEditFactorModal(true);
  };

  const handleDeleteFactor = (factor) => {
    setFactorToDelete(factor);
    setShowDeleteFactorModal(true);
  };

  const handleDeleteFactorConfirm = (confirmed) => {
    if (!confirmed || !factorToDelete) {
      setShowDeleteFactorModal(false);
      setFactorToDelete(null);
      return;
    }

    setLoaderDisplay(true);
    GHGEmissionMasterDataService.deleteFactor(datasetName, version, factorToDelete.id, companyId)
      .then((response) => {
        if (response.status === 200 || response.status === 204) {
          toast.success(t("GHGEmissionFactors.deleteSuccess.label"), {
            position: toast.POSITION.TOP_RIGHT,
          });
          if (selectedSource) {
            loadFactorsBySource();
          } else {
            loadFactors();
          }
        }
      })
      .catch((error) => {
      })
      .finally(() => {
        setLoaderDisplay(false);
        setShowDeleteFactorModal(false);
        setFactorToDelete(null);
      });
  };

  const handleFactorFormSuccess = () => {
    setShowAddFactorModal(false);
    setShowEditFactorModal(false);
    setSelectedFactor(null);
    if (selectedSource) {
      loadFactorsBySource();
    } else {
      loadFactors();
    }
  };

  const getSourceName = (sourceId) => {
    const source = sourcesForFactors.find(s => s.id === sourceId);
    return source ? source.name : sourceId;
  };

  const handleClearSourceFilter = () => {
    if (onClearSourceFilter) {
      onClearSourceFilter();
    }
    setFactorFilters({
      emissionSourceId: '',
      ghg: ''
    });
    setFactorsCurrentPage(1);
  };

  const handleSort = (field) => {
    if (sortField === field && order === 'asc') {
      setOrder('desc');
    } else {
      setOrder('asc');
    }
    setSortField(field);
  };

  return (
    <>
      {selectedSource && (
        <div className="alert alert-info d-flex justify-content-between align-items-center mb-3" role="alert">
          <div>
            <strong>{t("GHGEmissionFactors.viewingSourceFactors.label")}:</strong> {selectedSource.name}
          </div>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleClearSourceFilter}
            data-testid="clear-source-filter"
          >
            {t("GHGEmissionFactors.viewAllFactors.label")}
          </Button>
        </div>
      )}

      {/* Factors Filters - Only show when not viewing a specific source */}
      {!selectedSource && (
        <div className="filters-section mb-3 p-3 bg-light rounded">
          <div className="row g-2">
            <div className="col-md-4">
              <Form.Label>{t("GHGEmissionFactors.filters.emissionSource.label")}</Form.Label>
              <Typeahead
                id="emission-source-filter"
                labelKey="name"
                options={sourcesForFactors}
                placeholder={t("GHGEmissionFactors.filters.allSources.label")}
                selected={sourcesForFactors.filter(s => s.id === parseInt(factorFilters.emissionSourceId))}
                onChange={(selected) => {
                  handleFactorFilterChange('emissionSourceId', selected[0] ? selected[0].id : '');
                }}
                clearButton
              />
            </div>
            <div className="col-md-3">
              <Form.Label>{t("GHGEmissionFactors.filters.ghg.label")}</Form.Label>
              <Form.Control
                type="text"
                placeholder={t("GHGEmissionFactors.filters.ghg.placeholder.label")}
                value={factorFilters.ghg}
                onChange={(e) => handleFactorFilterChange('ghg', e.target.value)}
              />
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setFactorFilters({
                    emissionSourceId: '',
                    ghg: ''
                  });
                  setFactorsCurrentPage(1);
                }}
              >
                {t("Clear.label")}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="table-container">
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>{t("GHGEmissionFactors.table.emissionSource.label")}</th>
              <th>
                <Link to="#" onClick={() => handleSort('ghg')}>
                  {t("GHGEmissionFactors.table.ghg.label")}
                  <BiSort />
                </Link>
              </th>
              <th>
                <Link to="#" onClick={() => handleSort('efValue')}>
                  {t("GHGEmissionFactors.table.efValue.label")}
                  <BiSort />
                </Link>
              </th>
              <th>
                <Link to="#" onClick={() => handleSort('unit')}>
                  {t("GHGEmissionFactors.table.unit.label")}
                  <BiSort />
                </Link>
              </th>
              <th>
                <Link to="#" onClick={() => handleSort('scope')}>
                  {t("GHGEmissionFactors.table.scope.label")}
                  <BiSort />
                </Link>
              </th>
              <th>{t("GHGEmissionFactors.table.gwpBasis.label")}</th>
              <th>{t("GHGEmissionFactors.table.notes.label")}</th>
              <th>{t("Actions.label")}</th>
            </tr>
          </thead>
          <tbody>
            {factors.length > 0 ? (
              factors.map((factor) => (
                <tr key={factor.id}>
                  <td>{getSourceName(factor.emissionSourceId)}</td>
                  <td>
                    <Badge bg="primary">{factor.ghg || '-'}</Badge>
                  </td>
                  <td>{factor.efValue || '-'}</td>
                  <td>{factor.unit || '-'}</td>
                  <td>
                    <Badge bg="info">{factor.scope || '-'}</Badge>
                  </td>
                  <td>{factor.gwpBasis || '-'}</td>
                  <td>{factor.notes || '-'}</td>
                  <td>
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>{t("Edit.label")}</Tooltip>}
                    >
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => handleEditFactor(factor)}
                        className="p-1"
                        data-testid={`edit-factor-${factor.id}`}
                      >
                        <BiPencil />
                      </Button>
                    </OverlayTrigger>
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>{t("Delete.label")}</Tooltip>}
                    >
                      <Button
                        variant="link"
                        size="sm"
                        className="text-danger p-1"
                        onClick={() => handleDeleteFactor(factor)}
                        data-testid={`delete-factor-${factor.id}`}
                      >
                        <BiTrash />
                      </Button>
                    </OverlayTrigger>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center">
                  <div className="Nodatafound_box">
                    <h6>{t("NoData.label")}</h6>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {factors.length > 0 && !selectedSource && (
        <div className="p-2 List_footer_count">
          <PaginationComponent
            itemsTotalCount={factorsTotalCount || factors.length}
            itemsPerPage={factorsItemsPerPage}
            currentPage={factorsCurrentPage}
            setCurrentPage={setFactorsCurrentPage}
            setItemsPerPage={setFactorsItemsPerPage}
            pageClicked={(pageNum, perPage) => {
              if (pageNum && pageNum !== factorsCurrentPage) {
                setFactorsCurrentPage(pageNum);
              }
              if (perPage && perPage !== factorsItemsPerPage) {
                setFactorsItemsPerPage(perPage);
                setFactorsCurrentPage(1);
              }
            }}
            alwaysShown={true}
          />
        </div>
      )}

      {/* Modals */}
      <FactorFormModal
        show={showAddFactorModal || showEditFactorModal}
        onHide={() => {
          setShowAddFactorModal(false);
          setShowEditFactorModal(false);
          setSelectedFactor(null);
          if (onHideAddFactor) {
            onHideAddFactor();
          }
        }}
        datasetName={datasetName}
        version={version}
        factor={selectedFactor}
        sources={sourcesForFactors}
        selectedSource={selectedSource}
        onSuccess={handleFactorFormSuccess}
        companyId={companyId}
      />

      {showDeleteFactorModal && (
        <ConfirmationModal
          modalShow={true}
          messageText={t("GHGEmissionFactors.deleteModal.message.label")}
          callbackModal={handleDeleteFactorConfirm}
          title={t("GHGEmissionFactors.deleteModal.title.label")}
          confirmBtn={t("Delete.label")}
          CancelBtn={t("Cancel.label")}
        />
      )}
    </>
  );
};

export default withTranslation()(GHGEmissionFactors);
